/**
 * Character store for Idle Depths.
 *
 * Manages characters with localStorage-first pattern:
 * - All changes saved to localStorage immediately (game works offline)
 * - Users with server sessions additionally sync to server (backup)
 */

import {
  type CharacterData,
  type CharacterExport,
  type CharacterSummary,
  type CharacterStat,
  type CurrentActivity,
  type SkillProgress,
  MAX_CHARACTERS_PER_USER,
  CHARACTER_EXPORT_VERSION,
  createCharacter,
  createCharacterSummary,
  validateCharacterExport,
  getStatDefinition,
  initializeSkills,
} from '@idle-depths/shared';
import * as Sentry from '@sentry/react';
import { makeAutoObservable, runInAction } from 'mobx';
import { nanoid } from 'nanoid';

import type { RootStore } from './RootStore';

import { getVanillaClient } from '@/lib/trpc';
import { LocalStorageService } from '@/services/LocalStorageService';

/**
 * Character store.
 *
 * Manages character data with localStorage as primary storage.
 * Server sync is backup only - game works fully offline.
 */
export class CharacterStore {
  /** All characters indexed by ID */
  characters: Map<string, CharacterData> = new Map();

  /** Currently active character ID */
  activeCharacterId: string | null = null;

  /** Whether characters are being loaded */
  isLoading = false;

  /** Whether syncing to server */
  isSyncing = false;

  /** Last error message */
  error: string | null = null;

  /** Sync debounce timer */
  private syncTimer: number | null = null;

  /** Sync debounce delay (ms) */
  private readonly SYNC_DEBOUNCE = 2000;

  /** Stale threshold for server sync (5 minutes) */
  private readonly STALE_THRESHOLD = 5 * 60 * 1000;

  /** Last server sync timestamp */
  private lastServerSync = 0;

  /** Reference to root store */
  private readonly rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, {
      rootStore: false,
      SYNC_DEBOUNCE: false,
      STALE_THRESHOLD: false,
    } as Record<string, false>);
  }

  // ===========================================================================
  // Computed Properties
  // ===========================================================================

  /**
   * Get the active character (or null if none selected).
   */
  get activeCharacter(): CharacterData | null {
    if (!this.activeCharacterId) {
      return null;
    }
    return this.characters.get(this.activeCharacterId) ?? null;
  }

  /**
   * Get all characters as an array sorted by slot index.
   */
  get characterList(): CharacterData[] {
    return Array.from(this.characters.values()).sort((a, b) => a.slotIndex - b.slotIndex);
  }

  /**
   * Get character summaries for selection screen.
   */
  get characterSummaries(): CharacterSummary[] {
    return this.characterList.map(createCharacterSummary);
  }

  /**
   * Whether the user can create a new character.
   */
  get canCreateCharacter(): boolean {
    return this.characters.size < MAX_CHARACTERS_PER_USER;
  }

  /**
   * Get current health of active character.
   */
  get currentHealth(): number {
    const character = this.activeCharacter;
    if (!character) {
      return 0;
    }
    return character.stats.health?.currentValue ?? 0;
  }

  /**
   * Get max health of active character.
   */
  get maxHealth(): number {
    const character = this.activeCharacter;
    if (!character) {
      return 0;
    }
    return character.stats.health?.maximumValue ?? 100;
  }

  /**
   * Whether any characters have pending sync.
   */
  get hasPendingSync(): boolean {
    return Array.from(this.characters.values()).some((character) => character.needsSync);
  }

  /**
   * Number of characters.
   */
  get characterCount(): number {
    return this.characters.size;
  }

  /**
   * Get the currency (gold) of the active character.
   */
  get currency(): number {
    return this.activeCharacter?.currency ?? 0;
  }

  // ===========================================================================
  // Lifecycle
  // ===========================================================================

  /**
   * Initialize the character store.
   *
   * 1. Load from localStorage (immediate, always works offline)
   * 2. If has server session, attempt server restore/merge
   */
  async initialize(): Promise<void> {
    runInAction(() => {
      this.isLoading = true;
      this.error = null;
    });

    try {
      // 1. Load from localStorage (immediate, always works)
      this.loadFromLocalStorage();

      // 2. If has server session, attempt server sync
      if (this.rootStore.authStore.hasServerUser) {
        await this.syncWithServer();
      }
    } catch (error) {
      Sentry.captureException(error, {
        tags: { context: 'character_store_init' },
      });
      runInAction(() => {
        this.error = 'Failed to initialize characters';
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  // ===========================================================================
  // CRUD Operations (localStorage-first)
  // ===========================================================================

  /**
   * Create a new character.
   *
   * @param name - Character name
   * @returns The created character
   * @throws Error if max characters reached or invalid name
   */
  createNewCharacter(name: string): CharacterData {
    if (!this.canCreateCharacter) {
      throw new Error(`Maximum ${String(MAX_CHARACTERS_PER_USER)} characters allowed`);
    }

    const trimmedName = name.trim();
    if (trimmedName.length < 3 || trimmedName.length > 20) {
      throw new Error('Character name must be 3-20 characters');
    }

    const id = nanoid();
    const slotIndex = this.findNextSlotIndex();
    const character = createCharacter(id, trimmedName, slotIndex);

    // Add to store
    this.characters.set(id, character);

    // Save to localStorage
    this.saveCharacterToLocalStorage(character);

    // Queue server sync
    this.queueServerSync();

    return character;
  }

  /**
   * Select a character as active.
   *
   * Initializes character data and starts the game tick loop.
   * This is async to ensure all data is loaded before ticking begins.
   *
   * @param id - Character ID to select
   */
  async selectCharacter(id: string): Promise<void> {
    const character = this.characters.get(id);
    if (!character) {
      throw new Error('Character not found');
    }

    // Stop ticking for previous character if any
    if (this.activeCharacterId && this.activeCharacterId !== id) {
      this.rootStore.gameStore.stopTicking();
      this.rootStore.bankStore.reset();
      this.rootStore.shopStore.reset();
    }

    // Update last played
    const now = Date.now();
    character.lastPlayedAt = now;
    character.updatedAt = now;
    character.needsSync = true;

    runInAction(() => {
      this.activeCharacterId = id;
    });

    // Save to localStorage
    this.saveCharacterToLocalStorage(character);
    LocalStorageService.setActiveCharacterId(id);

    // Initialize bank for new character - MUST complete before ticking
    await this.rootStore.bankStore.initialize();

    // Start game ticking with character ID for per-character offline tracking
    this.rootStore.gameStore.startTicking(id);

    // Queue server sync
    this.queueServerSync();
  }

  /**
   * Deselect the active character.
   *
   * Stops the game tick loop.
   */
  deselectCharacter(): void {
    if (!this.activeCharacterId) {
      return;
    }

    // Stop game ticking
    this.rootStore.gameStore.stopTicking();

    // Reset bank and shop stores
    this.rootStore.bankStore.reset();
    this.rootStore.shopStore.reset();

    this.activeCharacterId = null;
    LocalStorageService.setActiveCharacterId(null);
  }

  /**
   * Update a character's data.
   *
   * @param id - Character ID
   * @param updates - Partial character data to merge
   */
  updateCharacter(id: string, updates: Partial<CharacterData>): void {
    const character = this.characters.get(id);
    if (!character) {
      throw new Error('Character not found');
    }

    // Merge updates
    Object.assign(character, updates, {
      updatedAt: Date.now(),
      needsSync: true,
    });

    // Save to localStorage
    this.saveCharacterToLocalStorage(character);

    // Queue server sync
    this.queueServerSync();
  }

  /**
   * Delete a character.
   *
   * Stops the game tick loop if deleting the active character.
   *
   * @param id - Character ID to delete
   */
  async deleteCharacter(id: string): Promise<void> {
    const character = this.characters.get(id);
    if (!character) {
      throw new Error('Character not found');
    }

    // Remove from store
    this.characters.delete(id);

    // Clear active if this was the active character
    if (this.activeCharacterId === id) {
      // Stop game ticking
      this.rootStore.gameStore.stopTicking();

      // Reset bank and shop stores
      this.rootStore.bankStore.reset();
      this.rootStore.shopStore.reset();

      this.activeCharacterId = null;
      LocalStorageService.setActiveCharacterId(null);
    }

    // Remove from localStorage
    LocalStorageService.deleteCharacter(id);

    // Clear tick time for this character
    LocalStorageService.clearLastTickTime(id);

    // Delete from server if online
    if (this.rootStore.authStore.hasServerUser) {
      try {
        const client = getVanillaClient();
        await client.character.delete.mutate({ id });
      } catch (error) {
        // Log but don't fail - local deletion succeeded
        Sentry.captureException(error, {
          tags: { context: 'character_delete_server' },
        });
      }
    }
  }

  // ===========================================================================
  // Stat Helpers
  // ===========================================================================

  /**
   * Set a stat value on the active character.
   *
   * @param statId - Stat ID
   * @param value - New value
   */
  setStatValue(statId: string, value: number): void {
    const character = this.activeCharacter;
    if (!character) {
      return;
    }

    const stat = character.stats[statId];
    if (!stat) {
      // Create stat if it doesn't exist
      // TODO: Migrate to gameStore.content.get('stats', statId) when CharacterStore
      // is guaranteed to be called after GameStore initialization
      // eslint-disable-next-line @typescript-eslint/no-deprecated
      const definition = getStatDefinition(statId);
      if (!definition) {
        return;
      }

      character.stats[statId] = {
        statId,
        currentValue: value,
        maximumValue: definition.defaultMaximum,
      };
    } else {
      // Clamp value to min/max
      const clampedValue = stat.maximumValue !== null ? Math.min(value, stat.maximumValue) : value;
      stat.currentValue = Math.max(0, clampedValue);
    }

    character.updatedAt = Date.now();
    character.needsSync = true;

    this.saveCharacterToLocalStorage(character);
    this.queueServerSync();
  }

  /**
   * Modify a stat value by delta on the active character.
   *
   * @param statId - Stat ID
   * @param delta - Amount to add (can be negative)
   */
  modifyStatValue(statId: string, delta: number): void {
    const character = this.activeCharacter;
    if (!character) {
      return;
    }

    const stat = character.stats[statId];
    if (!stat) {
      return;
    }

    this.setStatValue(statId, stat.currentValue + delta);
  }

  /**
   * Get a stat from the active character.
   *
   * @param statId - Stat ID
   * @returns CharacterStat or undefined
   */
  getStat(statId: string): CharacterStat | undefined {
    return this.activeCharacter?.stats[statId];
  }

  // ===========================================================================
  // Currency Operations
  // ===========================================================================

  /**
   * Add currency (gold) to the active character.
   *
   * @param amount - Amount to add (must be positive)
   */
  addCurrency(amount: number): void {
    const character = this.activeCharacter;
    if (!character || amount <= 0) {
      return;
    }

    character.currency += amount;
    character.updatedAt = Date.now();
    character.needsSync = true;

    this.saveCharacterToLocalStorage(character);
    this.queueServerSync();
  }

  /**
   * Spend currency (gold) from the active character.
   *
   * @param amount - Amount to spend (must be positive)
   * @returns True if successful, false if insufficient funds
   */
  spendCurrency(amount: number): boolean {
    const character = this.activeCharacter;
    if (!character || amount <= 0) {
      return false;
    }

    if (character.currency < amount) {
      return false;
    }

    character.currency -= amount;
    character.updatedAt = Date.now();
    character.needsSync = true;

    this.saveCharacterToLocalStorage(character);
    this.queueServerSync();
    return true;
  }

  /**
   * Check if the active character can afford a given amount.
   *
   * @param amount - Amount to check
   * @returns True if character has sufficient funds
   */
  canAfford(amount: number): boolean {
    const character = this.activeCharacter;
    return character ? character.currency >= amount : false;
  }

  /**
   * Get current currency of the active character.
   */
  get currentCurrency(): number {
    return this.activeCharacter?.currency ?? 0;
  }

  // ===========================================================================
  // Equipment Operations
  // ===========================================================================

  /**
   * Set an equipment slot for the active character.
   *
   * @param slot - Equipment slot to set
   * @param itemId - Item ID to equip, or null to clear slot
   */
  setEquipment(slot: import('@idle-depths/shared').EquipmentSlot, itemId: string | null): void {
    const character = this.activeCharacter;
    if (!character) {
      return;
    }

    character.equipment[slot] = itemId;
    character.updatedAt = Date.now();
    character.needsSync = true;

    this.saveCharacterToLocalStorage(character);
    this.queueServerSync();
  }

  /**
   * Get equipped item in a slot.
   *
   * @param slot - Equipment slot to check
   * @returns Item ID or null if empty
   */
  getEquipped(slot: import('@idle-depths/shared').EquipmentSlot): string | null {
    return this.activeCharacter?.equipment[slot] ?? null;
  }

  /**
   * Get all equipped items.
   *
   * @returns Equipment object or null if no active character
   */
  get equipment(): import('@idle-depths/shared').CharacterEquipment | null {
    return this.activeCharacter?.equipment ?? null;
  }

  // ===========================================================================
  // Activity & Location Operations
  // ===========================================================================

  /**
   * Set the current activity for the active character.
   *
   * Used by the location system for travel and future systems for skilling/combat.
   *
   * @param activity - The new activity to set
   */
  setActivity(activity: CurrentActivity): void {
    const character = this.activeCharacter;
    if (!character) {
      return;
    }

    character.currentActivity = activity;
    character.updatedAt = Date.now();
    character.needsSync = true;

    this.saveCharacterToLocalStorage(character);
    this.queueServerSync();
  }

  /**
   * Update the progress of the current activity.
   *
   * Progress is a value from 0 to 1 representing completion percentage.
   *
   * @param progress - Progress value (0-1)
   */
  updateActivityProgress(progress: number): void {
    const character = this.activeCharacter;
    if (!character) {
      return;
    }

    character.currentActivity.progress = Math.max(0, Math.min(1, progress));
    character.updatedAt = Date.now();
    character.needsSync = true;

    this.saveCharacterToLocalStorage(character);
    this.queueServerSync();
  }

  /**
   * Update the current location for the active character.
   *
   * Called when travel completes or when teleporting.
   *
   * @param locationId - The new location ID
   */
  updateCurrentLocation(locationId: string): void {
    const character = this.activeCharacter;
    if (!character) {
      return;
    }

    character.currentLocationId = locationId;
    character.updatedAt = Date.now();
    character.needsSync = true;

    this.saveCharacterToLocalStorage(character);
    this.queueServerSync();
  }

  /**
   * Update travel progress for the active character.
   *
   * Updates both the overall progress and the current leg tracking.
   * This method ensures MobX properly tracks changes to nested activity data.
   *
   * @param progress - Overall progress value (0-1)
   * @param legIndex - Current leg index in the route
   * @param legProgress - Progress through current leg (0-1)
   */
  updateTravelProgress(progress: number, legIndex: number, legProgress: number): void {
    const character = this.activeCharacter;
    if (!character || character.currentActivity.type !== 'traveling') {
      return;
    }

    // Update progress
    character.currentActivity.progress = Math.max(0, Math.min(1, progress));

    // Update leg tracking in activity data
    const activityData = character.currentActivity.activityData;
    activityData.currentLegIndex = legIndex;
    activityData.currentLegProgress = Math.max(0, Math.min(1, legProgress));

    character.updatedAt = Date.now();
    character.needsSync = true;

    this.saveCharacterToLocalStorage(character);
    this.queueServerSync();
  }

  // ===========================================================================
  // Import/Export
  // ===========================================================================

  /**
   * Export a character to a downloadable format.
   *
   * @param id - Character ID
   * @returns CharacterExport object
   */
  exportCharacter(id: string): CharacterExport {
    const character = this.characters.get(id);
    if (!character) {
      throw new Error('Character not found');
    }

    return {
      version: CHARACTER_EXPORT_VERSION,
      exportedAt: Date.now(),
      character: {
        ...character,
        // Reset sync metadata for export
        lastSyncedAt: null,
        needsSync: false,
      },
    };
  }

  /**
   * Export all characters.
   *
   * @returns Array of CharacterExport objects
   */
  exportAllCharacters(): CharacterExport[] {
    return this.characterList.map((character) => this.exportCharacter(character.id));
  }

  /**
   * Import a character from an export file.
   *
   * @param exportData - The export data to import
   * @returns The imported character
   * @throws Error if invalid format or max characters reached
   */
  importCharacter(exportData: CharacterExport): CharacterData {
    // Validate export format
    if (!validateCharacterExport(exportData)) {
      throw new Error('Invalid character export format');
    }

    // Check character limit
    if (!this.canCreateCharacter) {
      throw new Error(`Maximum ${String(MAX_CHARACTERS_PER_USER)} characters reached`);
    }

    // Generate new ID and slot to avoid conflicts
    const now = Date.now();
    const character: CharacterData = {
      ...exportData.character,
      id: nanoid(),
      slotIndex: this.findNextSlotIndex(),
      createdAt: now,
      updatedAt: now,
      lastPlayedAt: now,
      lastSyncedAt: null,
      needsSync: true,
    };

    // Add to store
    this.characters.set(character.id, character);

    // Save to localStorage
    this.saveCharacterToLocalStorage(character);

    // Queue server sync
    this.queueServerSync();

    return character;
  }

  // ===========================================================================
  // LocalStorage Operations
  // ===========================================================================

  /**
   * Load characters from localStorage.
   *
   * Includes migration for characters created before skills were added.
   */
  private loadFromLocalStorage(): void {
    const characters = LocalStorageService.getCharacters();
    const activeId = LocalStorageService.getActiveCharacterId();

    runInAction(() => {
      this.characters.clear();
      for (const character of characters) {
        // Migrate characters from older versions
        // Cast to handle legacy data that may be missing skills (pre-v0.2.5)
        const existingSkills = (character as unknown as { skills?: Record<string, SkillProgress> })
          .skills;

        // Initialize or merge skills
        character.skills = initializeSkills(existingSkills);
        if (!existingSkills) {
          character.needsSync = true;
        }

        this.characters.set(character.id, character);
      }

      // Restore active character if it exists
      if (activeId && this.characters.has(activeId)) {
        this.activeCharacterId = activeId;
      } else {
        // Select first character if no active saved
        const firstCharacter = characters[0];
        if (firstCharacter) {
          this.activeCharacterId = firstCharacter.id;
          LocalStorageService.setActiveCharacterId(firstCharacter.id);
        }
      }
    });

    // Save migrated characters back to localStorage
    this.saveAllToLocalStorage();
  }

  /**
   * Save all characters to localStorage.
   */
  private saveAllToLocalStorage(): void {
    LocalStorageService.setCharacters(this.characterList);
  }

  /**
   * Save a single character to localStorage.
   */
  private saveCharacterToLocalStorage(character: CharacterData): void {
    LocalStorageService.upsertCharacter(character);
  }

  // ===========================================================================
  // Server Sync (Backup)
  // ===========================================================================

  /**
   * Sync with server (merge local and server data).
   *
   * Sync strategy:
   * 1. Get server characters
   * 2. Merge with local (most recent updatedAt wins)
   * 3. Push local changes to server
   */
  private async syncWithServer(): Promise<void> {
    if (!this.rootStore.authStore.hasServerUser) {
      return;
    }

    runInAction(() => {
      this.isSyncing = true;
    });

    try {
      const client = getVanillaClient();

      // Get server characters
      const serverResult = await client.character.list.query();
      const serverCharacters = serverResult.characters;

      // Merge with local
      this.mergeWithServer(serverCharacters);

      // Push local changes
      await this.pushToServer();

      this.lastServerSync = Date.now();
    } catch (error) {
      Sentry.captureException(error, {
        tags: { context: 'character_server_sync' },
      });
    } finally {
      runInAction(() => {
        this.isSyncing = false;
      });
    }
  }

  /**
   * Merge server characters with local.
   *
   * For conflicts, most recent updatedAt wins.
   */
  private mergeWithServer(serverCharacters: CharacterData[]): void {
    const serverMap = new Map<string, CharacterData>();
    for (const character of serverCharacters) {
      serverMap.set(character.id, character);
    }

    runInAction(() => {
      // Check for server-only characters (need to pull)
      for (const serverChar of serverCharacters) {
        const localChar = this.characters.get(serverChar.id);

        if (!localChar) {
          // Server-only: pull to local
          this.characters.set(serverChar.id, {
            ...serverChar,
            lastSyncedAt: Date.now(),
            needsSync: false,
          });
        } else if (serverChar.updatedAt > localChar.updatedAt) {
          // Server is newer: use server data
          this.characters.set(serverChar.id, {
            ...serverChar,
            lastSyncedAt: Date.now(),
            needsSync: false,
          });
        }
        // If local is newer, it will be pushed in pushToServer
      }
    });

    // Save merged data to localStorage
    this.saveAllToLocalStorage();
  }

  /**
   * Push local changes to server.
   */
  private async pushToServer(): Promise<void> {
    const charactersNeedingSync = this.characterList.filter((c) => c.needsSync);

    if (charactersNeedingSync.length === 0) {
      return;
    }

    try {
      const client = getVanillaClient();
      const result = await client.character.syncBatch.mutate(charactersNeedingSync);

      // Mark successful syncs
      runInAction(() => {
        for (const syncResult of result.results) {
          if (syncResult.success) {
            const character = this.characters.get(syncResult.characterId);
            if (character) {
              character.lastSyncedAt = result.syncedAt;
              character.needsSync = false;
            }
          }
        }
      });

      // Save updated sync status to localStorage
      this.saveAllToLocalStorage();
    } catch (error) {
      Sentry.captureException(error, {
        tags: { context: 'character_push_to_server' },
      });
    }
  }

  /**
   * Queue a server sync (debounced).
   */
  private queueServerSync(): void {
    if (!this.rootStore.authStore.hasServerUser) {
      return;
    }

    if (this.syncTimer) {
      clearTimeout(this.syncTimer);
    }

    this.syncTimer = window.setTimeout(() => {
      void this.pushToServer();
    }, this.SYNC_DEBOUNCE);
  }

  /**
   * Check if server sync is stale and refresh if needed.
   *
   * Called on window focus.
   */
  async refreshIfStale(): Promise<void> {
    if (!this.rootStore.authStore.hasServerUser) {
      return;
    }

    const timeSinceSync = Date.now() - this.lastServerSync;
    if (timeSinceSync > this.STALE_THRESHOLD) {
      await this.syncWithServer();
    }
  }

  /**
   * Force immediate sync to server.
   */
  async syncNow(): Promise<void> {
    if (this.syncTimer) {
      clearTimeout(this.syncTimer);
      this.syncTimer = null;
    }

    await this.syncWithServer();
  }

  /**
   * Restore all characters from server.
   *
   * Use for new device or after local data loss.
   * WARNING: This replaces all local data!
   */
  async restoreFromServer(): Promise<void> {
    if (!this.rootStore.authStore.hasServerUser) {
      throw new Error('Must be logged in to restore from server');
    }

    runInAction(() => {
      this.isLoading = true;
      this.error = null;
    });

    try {
      const client = getVanillaClient();
      const result = await client.character.restore.query();

      runInAction(() => {
        this.characters.clear();
        for (const character of result.characters) {
          this.characters.set(character.id, character);
        }

        // Select first character if any
        const firstRestoredCharacter = result.characters[0];
        if (firstRestoredCharacter) {
          this.activeCharacterId = firstRestoredCharacter.id;
        } else {
          this.activeCharacterId = null;
        }
      });

      // Save to localStorage
      this.saveAllToLocalStorage();
      LocalStorageService.setActiveCharacterId(this.activeCharacterId);

      this.lastServerSync = Date.now();
    } catch (error) {
      Sentry.captureException(error, {
        tags: { context: 'character_restore' },
      });
      runInAction(() => {
        this.error = 'Failed to restore from server';
      });
      throw error;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  // ===========================================================================
  // Utility Methods
  // ===========================================================================

  /**
   * Find the next available slot index.
   */
  private findNextSlotIndex(): number {
    const usedSlots = new Set(this.characterList.map((c) => c.slotIndex));
    for (let i = 0; i < MAX_CHARACTERS_PER_USER; i++) {
      if (!usedSlots.has(i)) {
        return i;
      }
    }
    return this.characters.size;
  }

  /**
   * Reset store to initial state.
   *
   * Stops the game tick loop if running.
   */
  reset(): void {
    if (this.syncTimer) {
      clearTimeout(this.syncTimer);
      this.syncTimer = null;
    }

    // Stop game ticking
    this.rootStore.gameStore.stopTicking();

    this.characters.clear();
    this.activeCharacterId = null;
    this.isLoading = false;
    this.isSyncing = false;
    this.error = null;
    this.lastServerSync = 0;
  }
}
